import { Link } from "react-router-dom";
import { motion } from "motion/react";
import { Thumb } from "./Thumb";
import { Skeleton } from "../../components/ui";
import { haptic } from "../../telegram/haptics";
import { listItem, pressTile } from "../../motion";
import { isLatinScript, recordDir } from "../../i18n/format";
import { useT } from "../../i18n/strings";
import type { ArtworkRow as Row } from "../../api/types";
import s from "./artworks.module.css";

/**
 * The gallery shape. Where the row is for scanning an inventory, the card is
 * for looking at the work: the image takes the whole tile and the text sits
 * under it, unboxed, the way a wall label sits under a painting.
 */
export function ArtworkCard({
  artwork,
  onMediaError,
}: {
  artwork: Row;
  onMediaError?: () => void;
}) {
  const t = useT();
  const latin = isLatinScript(artwork.title);

  return (
    <motion.div variants={listItem} exit="exit" layout="position">
      <motion.div {...pressTile}>
        <Link to={`/artwork/${artwork.id}`} className={s.card} onClick={() => haptic.tap()}>
          {/* The mat is a real element rather than padding on the thumb: the
              print has to cast its own hairline shadow onto the board, and a
              padding box cannot be shadowed from the inside. */}
          <span className={`${s.cardMat} paper`}>
            <Thumb
              media={artwork.primary_media}
              layoutId={artwork.primary_media ? `hero-${artwork.primary_media.id}` : undefined}
              onMediaError={onMediaError}
              className={s.cardThumb}
              iconSize={32}
            />
          </span>
          <div className={s.cardBody}>
            <span
              className={[s.cardTitle, latin && s.titleLatin, "bidi"].filter(Boolean).join(" ")}
              dir={recordDir(artwork.title || t.detail.untitled)}
            >
              {artwork.title || t.detail.untitled}
            </span>
            <span className={s.cardMeta}>
              <span
                className={`${s.artist} bidi`}
                dir={recordDir(artwork.artist_name || t.detail.unknownArtist)}
              >
                {artwork.artist_name || t.detail.unknownArtist}
              </span>
              {artwork.year_display && (
                <span className={`mono ${s.year}`}>{artwork.year_display}</span>
              )}
            </span>
          </div>
        </Link>
      </motion.div>
    </motion.div>
  );
}

/** The mat is part of the shape, so it is part of the placeholder too — the
    card must not change size when the work lands in it. */
export function ArtworkCardSkeleton() {
  return (
    <div className={s.skeletonCard}>
      <span className={`${s.cardMat} paper`}>
        <Skeleton height="100%" radius="2px" />
      </span>
      <Skeleton height={13} width="70%" radius="4px" />
      <Skeleton height={11} width="45%" radius="4px" />
    </div>
  );
}
